/**
 * Project setup redesign — Track E (environments, governance, AI team, members): namespaces `projectEnvironments`, `projectGovernance`, `projectAiTeam`, `projectMembers`.
 *
 * Top-level namespaces owned by this track only. `settingsAreasNl` is typed against
 * `settingsAreasEn`, so a key missing in Dutch fails typecheck exactly as it does in
 * the main dictionary.
 */
export const settingsAreasEn = {
  projectEnvironments: {
    where: {
      title: "Where changes are tried",
      subtitle: "FixControl tests proposed changes in an isolated copy of your project before anything is pushed. Your live systems are not touched.",
      testingTitle: "Development testing",
      statusLabel: "Status",
      providerLabel: "Runs on",
      verifiedOn: "Last checked successfully on {date}.",
      runCheck: "Test the safe copy",
      connectRepository: "Connect repository",
      runCheckRunning: "Testing…",
      checkStarted: "The test has started. It usually takes a few minutes; this page updates when it is done.",
      checkFailed: "The test could not be started. {reason}",
      checkFailedGeneric: "Please try again in a moment.",
      checkNoPermission: "Only people who manage this project's settings can start a test.",
      provider: {
        cloud: "FixControl's cloud test environment",
        server: "FixControl's own test server",
        own: "Your own Kubernetes environment",
        both: "FixControl's safe copy first, then your own environment",
      },
      modeTitle: "What may FixControl do with your code?",
      modeHint: "This decides what happens after a change has been tried. It applies to this project only.",
      modeSaveHint: "Saved with “Save changes” at the bottom of the page.",
      strategyTitle: "Where the safe copy runs",
    },
    runningCopies: {
      none: "Running copies of this project: none yet (technical details)",
    },
    testing: {
      state: {
        ready: "Ready",
        checking: "Testing now",
        needsAttention: "Needs attention",
        notTested: "Not tested yet",
        noCodebase: "Not set up yet",
        notNeeded: "Not needed for this project",
        unknown: "Unknown",
      },
      detail: {
        ready: "FixControl can start this project in a safe copy and try changes there.",
        checking: "FixControl is starting this project in a safe copy to see what it needs.",
        needsAttention: "The last test stopped before the project started. Open the technical details on the Code page, or test again.",
        notTested: "FixControl has not started this project in a safe copy yet. Test it once so changes can be tried before they reach you.",
        noCodebase: "Connect a repository first. Then FixControl can try proposed changes in a safe copy before anything reaches you.",
        notNeeded: "This project answers customer questions and has no code, so there is nothing to test. Connect a repository if you also want FixControl to propose and test code fixes.",
        unknown: "The test status could not be loaded.",
      },
    },
    preview: {
      title: "Preview",
      subtitle: "Preview lets you open the proposed change in your browser before it is approved.",
      label: "Preview available",
      state: {
        available: "Available",
        notYet: "Not available yet",
        notForProject: "Not available for this type of project",
        off: "Switched off",
        unknown: "Unknown",
      },
      reason: {
        available: "Each proposed change gets a private link to a running copy of your application with the change in it.",
        noCodebase: "This project has no codebase, so there is no application to open.",
        noCodebaseSupport: "This project answers customer questions, so there is no application to open. Connect a repository to enable testing and preview.",
        noCodebaseYet: "Connect a repository first. Once FixControl has started it in a safe copy, each proposed change gets a preview link.",
        switchedOff: "Previews are switched off for your organization. An administrator can switch them on in the organization settings.",
        notVerified: "FixControl first needs to start this project once in a safe copy. Use “Test the safe copy” above.",
        checking: "FixControl is starting this project in a safe copy right now. Preview becomes available when that succeeds.",
        nothingToOpen: "This project does not start anything a browser can open, such as a website or web app.",
        kind_android_app: "This project builds an Android app. Previews currently only work for websites and web apps.",
        kind_ios_app: "This project builds an iOS app. Previews currently only work for websites and web apps.",
        kind_desktop_app: "This project builds a desktop app. Previews currently only work for websites and web apps.",
        kind_http: "Each proposed change gets a private link to a running copy of your application with the change in it.",
        unknown: "The preview status could not be loaded.",
      },
      liveNow: "{n} preview(s) open right now.",
      companySetting: "Organization setting",
    },
    tests: {
      title: "Test environments",
    },
    advanced: {
      summary: "Advanced: technical details",
      hint: "Only needed if your technical team asks for it.",
      runsOnTitle: "Where tests and previews run",
      plane: {
        aws: "AWS Fargate: one short-lived task per test or preview, removed when it is done.",
        server: "Docker on the FixControl server.",
      },
      containerLimitsTitle: "Container limits",
      containerLimitsManaged: "Container limits: managed by FixControl.",
      validationTitle: "Validation and runtime details",
    },
    movedFromAdvanced: "Testing, validation and container limits moved to Environments",
    loadError: "Couldn't load this project's environment status.",
    retry: "Retry",
  },
  projectGovernance: {
    title: "Approvals & rules",
    subtitle: "What needs a person's approval on this project, who can give it, and where requests arrive.",
    customerReplies: {
      title: "Replies to customers",
      subtitle: "Whether a reply needs an approval before it goes out, and where that request appears.",
      deliveryOn: "Requests appear in:",
      deliveryOff: "Turn approval on, and requests appear in:",
      inapp: "FixControl",
      email: "e-mail to administrators and managers",
      chat: "{name} ({provider})",
      chatUnknown: "chat channels could not be checked",
      channelIdle: "{channels} is linked for approvals, but replies to customers need no approval now, so nothing arrives there for them.",
      noApprovers: "Nobody can approve right now: there is no active administrator or manager. Replies would keep waiting.",
      enableAi: "Require approval for AI replies",
      enableFailed: "Could not save. Try again, or change it under Customer communication.",
      loadError: "Could not load what happens to replies to customers.",
    },
    summary: {
      usingCompany: "Using company policy",
      usingProject: "Custom policy for this project",
      companyHint: "This project follows the company-wide approval policy. Only customize it if this project needs stricter rules.",
      projectHint: "This project applies rules of its own on top of the company-wide policy.",
      supportOnlyHint: "This project's own rule only covers production deployments, which this project does not do.",
      decidersOne: "{count} person can approve",
      decidersOther: "{count} people can approve",
      decidersNone: "Nobody can approve",
      inApp: "Requests appear in FixControl",
      inAppAnd: "Requests appear in FixControl and {channels}",
      viewCompany: "View company policy",
      customize: "Customize for this project",
      edit: "Change project rule",
      reset: "Reset to company policy",
    },
    blocked: {
      title: "Approval is currently blocked",
      noDecider: "Only an active administrator or manager can approve, and your organization has none. Work that needs approval waits until someone has one of those roles.",
      action: "Manage people",
      noAccess: "Ask an administrator of your organization to give someone the administrator or manager role.",
      stagesTitle: "Some approvals can never be completed",
      stages: "Staged approvals are on, and the approval route \"{route}\" needs {required} different people to approve, one per stage. Only {available} can approve, so such a request can only end in a rejection. Give more people the administrator or manager role, or shorten the route.",
      stagesUnnamed: "Staged approvals are on, and an approval route needs {required} different people to approve, one per stage. Only {available} can approve, so such a request can only end in a rejection. Give more people the administrator or manager role, or shorten the route.",
    },
    projectRules: {
      title: "This project's own rules",
      scopeTitle: "Rules on this scope",
      hint: "A rule set here applies only after it is activated, and only when it is stricter than what the project inherits from the company policy.",
      hintRelax: "A rule set here applies only after it is activated. Your organization allows these rules to be less strict than the company policy.",
      summary: "{active} applying · {pending} waiting for review · {other} not applied",
      status: {
        active: "Applies",
        pendingReview: "Waiting for review",
        notStricter: "Not applied: not stricter than the company policy",
        companyOnly: "Not applied: only the company policy sets this",
        locked: "Not applied: fixed by the company policy",
      },
      instead: "Applies instead: {sentence}",
      activate: "Review and activate",
      activateConfirmTitle: "Activate these rules?",
      activateConfirmBody: "They start applying to new work. A rule that is not stricter than the company policy stays recorded but does not apply.",
      activateConfirmBodyRelax: "They start applying to new work, including rules less strict than the company policy — your organization allows that.",
      activateConfirm: "Activate",
      deactivate: "Stop applying",
      deactivateConfirmTitle: "Stop applying these rules?",
      deactivateConfirmBody: "They stay recorded, but new work follows the company policy again.",
      deactivateConfirm: "Stop applying",
      activated: "Activated. New work follows these rules.",
      deactivated: "Stopped. New work follows the company policy.",
      failed: "Couldn't change this. Nothing was changed.",
      pendingOne: "{count} project rule waits for review: it is recorded but does not apply yet.",
      pendingOther: "{count} project rules wait for review: they are recorded but do not apply yet.",
      notAppliedOne: "{count} project rule does not apply: it is not stricter than the company policy.",
      notAppliedOther: "{count} project rules do not apply: they are not stricter than the company policy.",
      relaxAllowed: "Your organization allows project rules to be less strict than the company policy.",
    },
    rules: {
      title: "What requires approval?",
      action: "Action",
      requirement: "Requirement",
      source: "Rule from",
      checksTitle: "Checks before a change is handed over",
      sourceCompany: "Company policy",
      sourceProject: "This project",
    },
    outcome: {
      startWork: "Start an AI plan",
      createPullRequest: "Code change or pull request",
      deployToProduction: "Production deployment",
      sendCustomerReply: "Send a customer reply",
      testInSafeCopy: "Test in a safe copy",
      runTests: "Run the tests",
    },
    requirement: {
      planWork: {
        always: "Always",
        riskyOrCritical: "High / critical risk only",
        never: "Never",
      },
      changeCode: {
        never: "Never",
        always: "Always",
        risky_only: "High / critical risk only",
        production_impact: "When it touches production",
        security_sensitive: "When it touches security files",
        external_integration: "When it touches an external integration",
        database_change: "When it changes the database schema",
      },
      releaseToProduction: {
        blockedWithoutQa: "Blocked until QA signs off",
        qaSignoff: "QA sign-off",
        approval: "Always",
        conditional: "Depends on the approval rule",
        open: "Never",
      },
      contactCustomers: {
        never: "Always — a person sends it",
        draftOnly: "Always — a person sends it",
        highConfidence: "Below {autosend}% confidence",
        lowRisk: "Unless it is low risk",
      },
      runSandbox: {
        required: "Required",
        optional: "Optional",
      },
      runTests: {
        required: "Required",
        optional: "Optional",
      },
    },
    kind: {
      open: "Goes ahead on its own",
      sometimes: "Approval in some cases",
      approval: "Requires approval",
      person: "Needs a person",
      check: "Not allowed: this check is required",
      optional: "Allowed: this check is not required",
    },
    approvers: {
      title: "Who can approve?",
      subtitle: "Administrators and managers of your organization can approve everything on this page.",
      roleAdmin: "Administrator",
      roleManager: "Manager",
      canApprove: "Can approve",
      unassigned: "No user assigned",
      unassignedHint: "Requests name this role, but an administrator or manager decides them.",
      unassignedHintStaged: "Nobody is assigned this role, so its stage falls back to the stage's fallback role, or to any administrator or manager.",
      stagedOn: "Staged approvals are on: a request whose approval route has stages is approved stage by stage, in order, by the people assigned each role — one person per stage. Anyone who can approve can say no.",
      none: "Nobody in your organization is an active administrator or manager.",
      orgLevel: "Approver roles are managed at organization level.",
      manage: "Manage organization approvers",
    },
    holderCountOne: "{count} person",
    holderCountOther: "{count} people",
    fixApprovers: {
      who: "Only an organization administrator can change who approves.",
      action: "Change who approves (company-wide)",
    },
    advisory: "This policy setting is not enforced yet: your inbox rules decide whether a reply is sent automatically.",
    /** Under a row: what the runtime really does with it. */
    enforcement: {
      changeCode: "This setting does not hold back code changes: a person signs off a change when its issue is flagged as risky or your organization asks for a sign-off on every commit or merge, otherwise the review agent approves it.",
      qaSignoff: "Recorded in the policy, but nothing checks for a QA sign-off yet.",
      sandbox: "Not decided by this setting: FixControl only applies a change after it passed in a safe copy.",
      tests: "Recorded in the policy, but nothing holds a change back when it has no tests.",
      missionDeploys: "Applies only when a change FixControl made for this project is deployed to production.",
    },
    override: {
      fieldLabel: "Approval before a change FixControl made goes to production",
      hint: "This rule decides when a person must approve before a change FixControl made for this project is deployed to production. It does not hold back code changes or pull requests.",
      hintEngine: "Because risk rules may require a plan approval in your organization, it can also make a new plan wait for approval.",
      save: "Save and apply",
      saving: "Saving…",
      cancel: "Cancel",
      saved: "Saved and activated. Production deployments of changes FixControl makes for this project follow this rule.",
      savedEngine: "Saved and activated. Production deployments of changes FixControl makes for this project follow this rule, and it can make new plans wait for approval.",
      resetDone: "Reset. This project follows the company policy again.",
      failed: "Couldn't save this rule. Nothing was changed.",
      resetConfirmTitle: "Reset to company policy?",
      resetConfirmBody: "This removes this project's own approval rule. The project then follows the company-wide policy ({value}).",
      resetConfirm: "Reset",
      lockedByCompany: "Your organization has fixed this rule for every project, so it cannot be changed here.",
      noPermission: "Only administrators can change approval rules. Ask one of them if this project needs different rules.",
    },
    channels: {
      title: "Approval channels",
      subtitle: "Approval requests always stay available in FixControl. Add a Slack or Teams channel, or turn approval e-mail on, to also get them there.",
      inAppTitle: "FixControl",
      inAppHint: "On the issue and its mission. Everyone who can decide, and whoever started the work, gets a notification.",
      active: "Active",
      noneExtra: "No other channel is used. That is optional: every request waits in FixControl.",
      loadFailed: "Couldn't load which other channels are used. Every request still waits in FixControl.",
      add: "Add approval channel",
      used: "Used",
      notUsed: "Not used",
      depends: "Depends on the approval type",
      provider: { email: "E-mail", slack: "Slack", teams: "Microsoft Teams" },
      gateType: { plan: "AI plan", risky_patch: "Code change", deployment: "Deployment" },
      reason: {
        route: "Chosen by your approval route",
        originThread: "In the request's own Slack thread",
        projectChannel: "This project's channel",
        projectChannelNamed: "This project's channel {target}",
        defaultChannel: "Your workspace's default channel",
        emailOn: "To everyone who can decide",
        routeExcludes: "Your approval route does not use it",
        emailOff: "Approval e-mail is not turned on",
        mailNotConfigured: "No outgoing mail is set up",
        notConnected: "Not connected",
        noTarget: "Connected, but no channel to post in",
        noRecipients: "Nobody to send it to",
        always: "Always",
      },
      boundTitle: "Channels set up for this project",
      bindingUnused: "Not used for approval requests: approval e-mail goes to the people who can decide, each with their own links. You can remove this.",
      timeout: {
        none: "No time limit: a request waits until someone decides.",
        rule: "If nobody decides within {minutes} minutes: {action}.",
        action: {
          escalate: "a reminder goes to the managers (to the administrators when there are no managers)",
          notify_fallback: "a reminder goes to the managers (to the administrators when there are no managers)",
          auto_reject: "the request is rejected automatically",
          pause: "it keeps waiting",
        },
        automatic: "Checked automatically every five minutes.",
        manual: "Not checked automatically: an administrator can turn this on in the company governance settings.",
      },
    },
    roleAssignments: {
      title: "Who holds which role",
      subtitle: "With staged approvals on, a stage is decided by the people holding its role. Nobody → the stage's fallback role → any administrator or manager. One person approves at most one stage of a request, so a route needs as many different administrators and managers as it has stages. Only administrators and managers can hold a role.",
      unassigned: "Nobody — falls back",
      noCandidates: "Nobody in your organization is an active administrator or manager.",
      failed: "Couldn't change this. Nothing was changed.",
    },
    automation: {
      title: "How approval rules are applied",
      subtitle: "Four switches for the whole organization. Each one changes what work must pass, so each asks first.",
      on: "On",
      off: "Off",
      turnOn: "Turn on",
      turnOff: "Turn off",
      failed: "Couldn't change this. Nothing was changed.",
      timeouts: {
        label: "Apply approval timeouts and escalations automatically",
        hint: "Every five minutes: approvals that waited longer than your approval timeout get the action your policy names (a reminder to the managers, or the administrators when there are none, or an automatic rejection), and your escalation rules for stuck missions, inactive issues, issues past their time limit and exhausted retries act. Off: they only act when someone runs the governance sweep.",
        confirmOn: "Apply approval timeouts automatically?",
        confirmOnBody: "Approvals that are already overdue are handled on the next check, within five minutes.",
        confirmOff: "Stop applying approval timeouts automatically?",
        confirmOffBody: "Overdue approvals then keep waiting until someone decides or runs the governance sweep.",
      },
      staged: {
        label: "Enforce staged approvals",
        hint: "A gate whose approval route has stages is decided stage by stage: by the people who hold that role (see Who holds which role), in order, one stage per person, each with its own deadline. A no rejects at once. Off: one decision by an administrator or manager settles a gate.",
        confirmOn: "Enforce staged approvals?",
        confirmOnBody: "New gates whose route has stages need every stage. Gates already open are not affected. Check who holds each role first, or stages fall back to any administrator or manager.",
        confirmOff: "Stop enforcing staged approvals?",
        confirmOffBody: "New gates are settled by one decision again. Gates opened while this was on stay staged until they are decided.",
      },
      engine: {
        label: "Risk rules may require a plan approval",
        hint: "Before a plan starts, your risk rules, conflicts and safety controls are checked at the run's project; when they say approval is needed, the plan waits for one even if the plan approval policy alone would let it start. This can only add an approval, never remove one.",
        confirmOn: "Let risk rules require plan approvals?",
        confirmOnBody: "New plans may wait for approval where they did not before. Plans already running are not affected.",
        confirmOff: "Stop letting risk rules require plan approvals?",
        confirmOffBody: "New plans follow the plan approval policy alone again.",
      },
      relax: {
        label: "Projects may be less strict than the company policy",
        hint: "Off: a project's own rule only applies when it is stricter than the company policy. On: an activated project rule also applies when it relaxes it.",
        confirmOn: "Allow projects to relax the company policy?",
        confirmOnBody: "Activated project rules that are less strict than the company policy start applying to new work at once.",
        confirmOff: "Only allow stricter project rules?",
        confirmOffBody: "Project rules that are less strict than the company policy stop applying to new work at once.",
      },
    },
    companyPage: {
      crumb: "Company-wide rules",
      bannerLead: "You are changing the rules for all projects. {project} can also have its own rules on its",
      bannerLink: "Approvals & rules page",
      bannerTail: ".",
      back: "Back to Approvals & rules",
      forbidden: "Only an administrator of your organization can view or change the rules for all projects.",
    },
    /** Shown instead of the company-policy link to someone who cannot open that page. */
    companyNoAccess: "Rules for all projects are managed by an administrator of your organization.",
    pointerFromUpdates: "Who approves, and where approvals are asked: see Approvals & rules",
    advanced: {
      summary: "Advanced: technical policy",
      hint: "The policy fields, their origin and the scope chain, as the policy engine sees them.",
    },
    movedFromAdvanced: "The approval policy for this project moved to Approvals & rules",
    loadError: "Couldn't load the approval rules for this project.",
    retry: "Retry",
  },
  projectAiTeam: {
    modeTitle: "What the AI may do with your code on this project",
    modeChange: "Change under Environments",
    agentsTitle: "Agents working on this project",
    agentsHint: "Agents and their skills are shared by all projects in your organization. You manage them in AI team.",
    manage: "Manage AI team",
    skillsLabel: "Skills",
    noSkills: "No extra skills",
    paused: "Paused",
    loadError: "Couldn't load the AI team.",
    role: {
      intake: "Intake — turns incoming emails, chats and tickets into clear, prioritized issues.",
      workflow: "Workflow — decides the next step for each issue and who acts next.",
      codebase: "Codebase — finds the code and history that matter for an issue.",
      dev: "Developer — prepares a code change and tries it in a safe copy.",
      cto: "Lead — plans the work and reviews risky changes before they go further.",
      qa: "QA — checks that a change works and does not break anything else.",
      support: "Support reply — drafts replies to customers for a person to approve.",
      security: "Security — checks changes for security risks.",
      release: "Release — follows a change on its way to production.",
      ux: "Design check — compares screens before and after a change.",
      other: "Helps with work on this project.",
    },
  },
  projectMembers: {
    title: "Members",
    subtitle: "People who work on this project, and what each of them may do here.",
    orgWide: "Administrators and managers can already see every project, because they manage the whole company. Add other colleagues here.",
    listTitle: "People on this project",
    empty: "Nobody has been added yet.",
    emptyHint: "Colleagues who are not administrators or managers only see this project once you add them.",
    roleLabel: "Role",
    noRole: "No project role",
    validUntilLabel: "Access until (optional)",
    validUntilShort: "until {date}",
    status: {
      active: "Active",
      suspended: "Paused",
      revoked: "Removed",
    },
    roles: {
      admin: {
        name: "Project administrator",
        explain: "Can connect this project to mailboxes and tools your organization already has, test them, add or remove members and approve work. Renaming the project, adding new company accounts and changing the approval rules is up to an organization administrator.",
      },
      approver: {
        name: "Approver",
        explain: "Can approve changes and replies on this project, but cannot manage its members.",
      },
      contributor: {
        name: "Contributor",
        explain: "Can work on this project's issues and draft replies, but cannot approve or change settings.",
      },
      viewer: {
        name: "Viewer",
        explain: "Can see this project, but cannot change anything.",
      },
    },
    abilities: {
      changeSettings: "Can also change this project's settings.",
      manageConnections: "Can also manage this project's connections.",
      changeRules: "Can also change this project's rules.",
    },
    add: {
      title: "Add someone",
      emailLabel: "Email address",
      emailPlaceholder: "colleague@company.com",
      submit: "Add to project",
      adding: "Adding…",
      added: "{name} was added to this project.",
    },
    changeRole: "Change role",
    roleChanged: "Role updated.",
    remove: "Remove from project",
    removeConfirmTitle: "Remove {name} from this project?",
    removeConfirm: "They will no longer see this project's issues. The removal is kept in the audit log, and you can add them again later.",
    removed: "{name} was removed from this project.",
    invite: {
      button: "Invite someone new",
      title: "Invite someone new",
      intro: "This creates a FixControl account for your whole organization and then adds the person to this project.",
      nameLabel: "Full name",
      emailLabel: "Email address",
      passwordLabel: "Temporary password",
      passwordHint: "At least 8 characters. Share it with them yourself; they can change it after signing in.",
      weakPassword: "The temporary password must be at least 8 characters.",
      exists: "An account with this email address already exists. Add it with the form above instead.",
      submit: "Create account and add",
      creating: "Creating account…",
      failed: "Couldn't create the account. {reason}",
      cannot: "Only administrators can create accounts for new colleagues. Ask an administrator to invite them, then add them here.",
      notFoundHint: "There is no account with this email address yet.",
    },
    readOnly: "You can see who works on this project. Only project administrators, administrators and managers can change it.",
    loadError: "Couldn't load the members of this project.",
    retry: "Retry",
    rolesGap: "Your organization has no {roles} role yet.",
    errors: {
      lastAdmin: "This person is the last project administrator. Make someone else project administrator first.",
      selfEdit: "You cannot change your own access to this project. Ask another administrator.",
      userNotFound: "There is no active account with this email address in your organization.",
      roleNotAssignable: "This role cannot be used on this project.",
      forbidden: "You are not allowed to change the members of this project.",
      generic: "Something went wrong. Nothing was changed.",
    },
    wizard: {
      title: "Add a member",
      stepOf: "Step {n} of {total}",
      stepPerson: "Who",
      stepRole: "Role",
      stepDone: "Done",
      next: "Next",
      back: "Back",
      close: "Close",
      done: "Done",
      finished: "{name} is now a member of this project as {role}.",
    },
  },
};

export const settingsAreasNl: typeof settingsAreasEn = {
  projectEnvironments: {
    where: {
      title: "Waar wijzigingen worden uitgeprobeerd",
      subtitle: "FixControl test voorgestelde wijzigingen in een afgeschermde kopie van je project voordat er iets wordt doorgezet. Je live-systemen worden niet aangeraakt.",
      testingTitle: "Ontwikkeltesten",
      statusLabel: "Status",
      providerLabel: "Draait op",
      verifiedOn: "Laatst met succes getest op {date}.",
      runCheck: "Veilige kopie testen",
      connectRepository: "Repository koppelen",
      runCheckRunning: "Bezig met testen…",
      checkStarted: "De test is gestart. Dat duurt meestal een paar minuten; deze pagina werkt zichzelf bij als hij klaar is.",
      checkFailed: "De test kon niet worden gestart. {reason}",
      checkFailedGeneric: "Probeer het zo nog eens.",
      checkNoPermission: "Alleen mensen die de instellingen van dit project beheren, kunnen een test starten.",
      provider: {
        cloud: "De cloud-testomgeving van FixControl",
        server: "De eigen testserver van FixControl",
        own: "Je eigen Kubernetes-omgeving",
        both: "Eerst de veilige kopie van FixControl, daarna je eigen omgeving",
      },
      modeTitle: "Wat mag FixControl met je code doen?",
      modeHint: "Dit bepaalt wat er gebeurt nadat een wijziging is uitgeprobeerd. Het geldt alleen voor dit project.",
      modeSaveHint: "Wordt opgeslagen met ‘Wijzigingen opslaan’ onderaan de pagina.",
      strategyTitle: "Waar de veilige kopie draait",
    },
    runningCopies: {
      none: "Draaiende kopieën van dit project: nog geen (technische details)",
    },
    testing: {
      state: {
        ready: "Klaar voor gebruik",
        checking: "Wordt nu getest",
        needsAttention: "Aandacht nodig",
        notTested: "Nog niet getest",
        noCodebase: "Nog niet ingesteld",
        notNeeded: "Niet nodig voor dit project",
        unknown: "Onbekend",
      },
      detail: {
        ready: "FixControl kan dit project in een veilige kopie starten en daar wijzigingen uitproberen.",
        checking: "FixControl start dit project nu in een veilige kopie om te zien wat het nodig heeft.",
        needsAttention: "De laatste test stopte voordat het project opstartte. Bekijk de technische details op de pagina Code, of test opnieuw.",
        notTested: "FixControl heeft dit project nog niet in een veilige kopie gestart. Test het één keer, zodat wijzigingen worden uitgeprobeerd voordat ze bij jou komen.",
        noCodebase: "Koppel eerst een repository. Daarna kan FixControl voorgestelde wijzigingen in een veilige kopie uitproberen voordat er iets bij jou komt.",
        notNeeded: "Dit project beantwoordt vragen van klanten en heeft geen code, dus er valt niets te testen. Koppel een repository als je wilt dat FixControl ook codefixes voorstelt en test.",
        unknown: "De teststatus kon niet worden geladen.",
      },
    },
    preview: {
      title: "Preview",
      subtitle: "Met een preview open je de voorgestelde wijziging in je browser voordat die wordt goedgekeurd.",
      label: "Preview beschikbaar",
      state: {
        available: "Beschikbaar",
        notYet: "Nog niet beschikbaar",
        notForProject: "Niet beschikbaar voor dit soort project",
        off: "Uitgeschakeld",
        unknown: "Onbekend",
      },
      reason: {
        available: "Elke voorgestelde wijziging krijgt een privélink naar een draaiende kopie van je applicatie met de wijziging erin.",
        noCodebase: "Dit project heeft geen codebase, dus er is geen applicatie om te openen.",
        noCodebaseSupport: "Dit project beantwoordt vragen van klanten, dus er is geen applicatie om te openen. Koppel een repository om testen en preview aan te zetten.",
        noCodebaseYet: "Koppel eerst een repository. Zodra FixControl die in een veilige kopie heeft gestart, krijgt elke voorgestelde wijziging een previewlink.",
        switchedOff: "Previews staan uit voor je organisatie. Een beheerder kan ze aanzetten in de organisatie-instellingen.",
        notVerified: "FixControl moet dit project eerst één keer in een veilige kopie starten. Gebruik ‘Veilige kopie testen’ hierboven.",
        checking: "FixControl start dit project nu in een veilige kopie. Zodra dat lukt, is preview beschikbaar.",
        nothingToOpen: "Dit project start niets wat een browser kan openen, zoals een website of webapp.",
        kind_android_app: "Dit project bouwt een Android-app. Previews werken nu alleen voor websites en webapps.",
        kind_ios_app: "Dit project bouwt een iOS-app. Previews werken nu alleen voor websites en webapps.",
        kind_desktop_app: "Dit project bouwt een desktop-app. Previews werken nu alleen voor websites en webapps.",
        kind_http: "Elke voorgestelde wijziging krijgt een privélink naar een draaiende kopie van je applicatie met de wijziging erin.",
        unknown: "De previewstatus kon niet worden geladen.",
      },
      liveNow: "Er staan nu {n} preview(s) open.",
      companySetting: "Organisatie-instelling",
    },
    tests: {
      title: "Testomgevingen",
    },
    advanced: {
      summary: "Geavanceerd: technische details",
      hint: "Alleen nodig als je technische team erom vraagt.",
      runsOnTitle: "Waar tests en previews draaien",
      plane: {
        aws: "AWS Fargate: één kortlevende taak per test of preview, die daarna wordt opgeruimd.",
        server: "Docker op de FixControl-server.",
      },
      containerLimitsTitle: "Containerlimieten",
      containerLimitsManaged: "Containerlimieten: beheerd door FixControl.",
      validationTitle: "Details over validatie en runtime",
    },
    movedFromAdvanced: "Testen, validatie en containerlimieten staan nu onder Omgevingen",
    loadError: "Kon de omgevingsstatus van dit project niet laden.",
    retry: "Opnieuw proberen",
  },
  projectGovernance: {
    title: "Goedkeuringen & regels",
    subtitle: "Wat op dit project de goedkeuring van een mens nodig heeft, wie die kan geven en waar verzoeken binnenkomen.",
    customerReplies: {
      title: "Antwoorden aan klanten",
      subtitle: "Of een antwoord eerst goedgekeurd moet worden, en waar dat verzoek dan verschijnt.",
      deliveryOn: "Verzoeken verschijnen in:",
      deliveryOff: "Zet je goedkeuring aan, dan verschijnen verzoeken in:",
      inapp: "FixControl",
      email: "e-mail aan beheerders en managers",
      chat: "{name} ({provider})",
      chatUnknown: "chatkanalen konden niet worden gecontroleerd",
      channelIdle: "{channels} is gekoppeld voor goedkeuringen, maar antwoorden aan klanten hoeven nu niet goedgekeurd te worden. Daar komt dus niets binnen voor klantantwoorden.",
      noApprovers: "Niemand kan nu goedkeuren: er is geen actieve beheerder of manager. Antwoorden blijven dan wachten.",
      enableAi: "Goedkeuring aanzetten voor AI-antwoorden",
      enableFailed: "Opslaan lukte niet. Probeer het opnieuw, of wijzig het bij Klantcommunicatie.",
      loadError: "Kon niet laden wat er met antwoorden aan klanten gebeurt.",
    },
    summary: {
      usingCompany: "Volgt het bedrijfsbeleid",
      usingProject: "Eigen beleid voor dit project",
      companyHint: "Dit project volgt het goedkeuringsbeleid voor het hele bedrijf. Pas het alleen aan als dit project strengere regels nodig heeft.",
      projectHint: "Dit project past eigen regels toe bovenop het beleid voor het hele bedrijf.",
      supportOnlyHint: "De eigen regel van een project gaat alleen over uitrol naar productie, en die doet dit project niet.",
      decidersOne: "{count} persoon kan goedkeuren",
      decidersOther: "{count} personen kunnen goedkeuren",
      decidersNone: "Niemand kan goedkeuren",
      inApp: "Verzoeken verschijnen in FixControl",
      inAppAnd: "Verzoeken verschijnen in FixControl en {channels}",
      viewCompany: "Bedrijfsbeleid bekijken",
      customize: "Aanpassen voor dit project",
      edit: "Projectregel wijzigen",
      reset: "Terug naar bedrijfsbeleid",
    },
    blocked: {
      title: "Goedkeuren is nu geblokkeerd",
      noDecider: "Alleen een actieve beheerder of manager kan goedkeuren, en je organisatie heeft er geen. Werk dat goedkeuring nodig heeft wacht tot iemand een van die rollen heeft.",
      action: "Mensen beheren",
      noAccess: "Vraag een beheerder van je organisatie om iemand de rol beheerder of manager te geven.",
      stagesTitle: "Sommige goedkeuringen kunnen nooit rond komen",
      stages: "Goedkeuring in fasen staat aan, en de goedkeuringsroute \"{route}\" heeft {required} verschillende mensen nodig om goed te keuren, één per fase. Er kunnen er maar {available} goedkeuren, dus zo'n verzoek kan alleen eindigen in een afwijzing. Geef meer mensen de rol beheerder of manager, of maak de route korter.",
      stagesUnnamed: "Goedkeuring in fasen staat aan, en een goedkeuringsroute heeft {required} verschillende mensen nodig om goed te keuren, één per fase. Er kunnen er maar {available} goedkeuren, dus zo'n verzoek kan alleen eindigen in een afwijzing. Geef meer mensen de rol beheerder of manager, of maak de route korter.",
    },
    projectRules: {
      title: "Eigen regels van dit project",
      scopeTitle: "Regels op dit niveau",
      hint: "Een regel die je hier instelt geldt pas na activering, en alleen als die strenger is dan wat het project van het bedrijfsbeleid erft.",
      hintRelax: "Een regel die je hier instelt geldt pas na activering. Je organisatie staat toe dat deze regels minder streng zijn dan het bedrijfsbeleid.",
      summary: "{active} van kracht · {pending} wacht op review · {other} niet toegepast",
      status: {
        active: "Van kracht",
        pendingReview: "Wacht op review",
        notStricter: "Niet toegepast: niet strenger dan het bedrijfsbeleid",
        companyOnly: "Niet toegepast: dit stelt alleen het bedrijfsbeleid in",
        locked: "Niet toegepast: vastgezet door het bedrijfsbeleid",
      },
      instead: "Geldt in plaats daarvan: {sentence}",
      activate: "Bekijken en activeren",
      activateConfirmTitle: "Deze regels activeren?",
      activateConfirmBody: "Ze gaan gelden voor nieuw werk. Een regel die niet strenger is dan het bedrijfsbeleid blijft vastgelegd maar geldt niet.",
      activateConfirmBodyRelax: "Ze gaan gelden voor nieuw werk, ook regels die minder streng zijn dan het bedrijfsbeleid — je organisatie staat dat toe.",
      activateConfirm: "Activeren",
      deactivate: "Niet meer toepassen",
      deactivateConfirmTitle: "Deze regels niet meer toepassen?",
      deactivateConfirmBody: "Ze blijven vastgelegd, maar nieuw werk volgt weer het bedrijfsbeleid.",
      deactivateConfirm: "Niet meer toepassen",
      activated: "Geactiveerd. Nieuw werk volgt deze regels.",
      deactivated: "Gestopt. Nieuw werk volgt het bedrijfsbeleid.",
      failed: "Kon dit niet wijzigen. Er is niets gewijzigd.",
      pendingOne: "{count} projectregel wacht op review: die is vastgelegd maar geldt nog niet.",
      pendingOther: "{count} projectregels wachten op review: ze zijn vastgelegd maar gelden nog niet.",
      notAppliedOne: "{count} projectregel geldt niet: die is niet strenger dan het bedrijfsbeleid.",
      notAppliedOther: "{count} projectregels gelden niet: ze zijn niet strenger dan het bedrijfsbeleid.",
      relaxAllowed: "Je organisatie staat toe dat projectregels minder streng zijn dan het bedrijfsbeleid.",
    },
    rules: {
      title: "Wat heeft goedkeuring nodig?",
      action: "Actie",
      requirement: "Vereiste",
      source: "Regel uit",
      checksTitle: "Controles voordat een wijziging wordt opgeleverd",
      sourceCompany: "Bedrijfsbeleid",
      sourceProject: "Dit project",
    },
    outcome: {
      startWork: "AI-plan starten",
      createPullRequest: "Codewijziging of pull request",
      deployToProduction: "Uitrol naar productie",
      sendCustomerReply: "Antwoord aan klant versturen",
      testInSafeCopy: "Testen in een veilige kopie",
      runTests: "De tests draaien",
    },
    requirement: {
      planWork: {
        always: "Altijd",
        riskyOrCritical: "Alleen bij hoog / kritiek risico",
        never: "Nooit",
      },
      changeCode: {
        never: "Nooit",
        always: "Altijd",
        risky_only: "Alleen bij hoog / kritiek risico",
        production_impact: "Als het productie raakt",
        security_sensitive: "Als het beveiligingsbestanden raakt",
        external_integration: "Als het een externe koppeling raakt",
        database_change: "Als het het databaseschema wijzigt",
      },
      releaseToProduction: {
        blockedWithoutQa: "Geblokkeerd tot QA akkoord geeft",
        qaSignoff: "Akkoord van QA",
        approval: "Altijd",
        conditional: "Hangt af van de goedkeuringsregel",
        open: "Nooit",
      },
      contactCustomers: {
        never: "Altijd — een mens verstuurt het",
        draftOnly: "Altijd — een mens verstuurt het",
        highConfidence: "Onder {autosend}% zekerheid",
        lowRisk: "Tenzij het laag risico is",
      },
      runSandbox: {
        required: "Verplicht",
        optional: "Optioneel",
      },
      runTests: {
        required: "Verplicht",
        optional: "Optioneel",
      },
    },
    kind: {
      open: "Gaat zelf door",
      sometimes: "Soms goedkeuring nodig",
      approval: "Goedkeuring nodig",
      person: "Een mens is nodig",
      check: "Niet toegestaan: deze controle is verplicht",
      optional: "Toegestaan: deze controle is niet verplicht",
    },
    approvers: {
      title: "Wie kan goedkeuren?",
      subtitle: "Beheerders en managers van je organisatie kunnen alles op deze pagina goedkeuren.",
      roleAdmin: "Beheerder",
      roleManager: "Manager",
      canApprove: "Kan goedkeuren",
      unassigned: "Niemand toegewezen",
      unassignedHint: "Verzoeken noemen deze rol, maar een beheerder of manager beslist erover.",
      unassignedHintStaged: "Niemand heeft deze rol, dus de fase valt terug op de terugvalrol van die fase, of op elke beheerder of manager.",
      stagedOn: "Goedkeuring in fasen staat aan: een verzoek waarvan de goedkeurroute fases heeft, wordt fase voor fase en in volgorde goedgekeurd door de mensen met die rol — één persoon per fase. Iedereen die mag goedkeuren kan nee zeggen.",
      none: "Niemand in je organisatie is een actieve beheerder of manager.",
      orgLevel: "Goedkeurrollen worden voor de hele organisatie beheerd.",
      manage: "Goedkeurders van de organisatie beheren",
    },
    holderCountOne: "{count} persoon",
    holderCountOther: "{count} personen",
    fixApprovers: {
      who: "Alleen een beheerder van je organisatie kan wijzigen wie goedkeurt.",
      action: "Wijzigen wie goedkeurt (voor het hele bedrijf)",
    },
    advisory: "Deze beleidsinstelling wordt nog niet afgedwongen: je inboxregels bepalen of een antwoord automatisch wordt verstuurd.",
    enforcement: {
      changeCode: "Deze instelling houdt codewijzigingen niet tegen: een mens keurt een wijziging goed als het issue als riskant is gemarkeerd of als je organisatie goedkeuring vraagt voor elke commit of merge, anders keurt de review-agent hem goed.",
      qaSignoff: "Vastgelegd in het beleid, maar nog niets controleert of QA akkoord gaf.",
      sandbox: "Niet bepaald door deze instelling: FixControl past een wijziging pas toe nadat die in een veilige kopie is geslaagd.",
      tests: "Vastgelegd in het beleid, maar niets houdt een wijziging tegen als er geen tests zijn.",
      missionDeploys: "Geldt alleen als een wijziging die FixControl voor dit project maakte naar productie wordt uitgerold.",
    },
    override: {
      fieldLabel: "Goedkeuring voordat een wijziging van FixControl naar productie gaat",
      hint: "Deze regel bepaalt wanneer een mens moet goedkeuren voordat een wijziging die FixControl voor dit project maakte naar productie wordt uitgerold. Codewijzigingen en pull requests houdt hij niet tegen.",
      hintEngine: "Omdat risicoregels in je organisatie een plangoedkeuring mogen vereisen, kan hij ook een nieuw plan op goedkeuring laten wachten.",
      save: "Opslaan en toepassen",
      saving: "Bezig met opslaan…",
      cancel: "Annuleren",
      saved: "Opgeslagen en geactiveerd. Uitrol naar productie van wijzigingen die FixControl voor dit project maakt volgt deze regel.",
      savedEngine: "Opgeslagen en geactiveerd. Uitrol naar productie van wijzigingen die FixControl voor dit project maakt volgt deze regel, en hij kan nieuwe plannen op goedkeuring laten wachten.",
      resetDone: "Teruggezet. Dit project volgt weer het bedrijfsbeleid.",
      failed: "Kon deze regel niet opslaan. Er is niets gewijzigd.",
      resetConfirmTitle: "Terug naar bedrijfsbeleid?",
      resetConfirmBody: "Hiermee verdwijnt de eigen goedkeuringsregel van dit project. Het project volgt dan het beleid voor het hele bedrijf ({value}).",
      resetConfirm: "Terugzetten",
      lockedByCompany: "Je organisatie heeft deze regel voor alle projecten vastgezet, dus hij kan hier niet worden gewijzigd.",
      noPermission: "Alleen beheerders kunnen goedkeuringsregels wijzigen. Vraag een van hen als dit project andere regels nodig heeft.",
    },
    channels: {
      title: "Goedkeuringskanalen",
      subtitle: "Goedkeuringsverzoeken blijven altijd beschikbaar in FixControl. Voeg een Slack- of Teams-kanaal toe, of zet goedkeurings-e-mail aan, om ze daar ook te krijgen.",
      inAppTitle: "FixControl",
      inAppHint: "Bij het issue en de bijbehorende missie. Iedereen die kan beslissen, en wie het werk startte, krijgt een melding.",
      active: "Actief",
      noneExtra: "Er wordt geen ander kanaal gebruikt. Dat is optioneel: elk verzoek wacht in FixControl.",
      loadFailed: "Kon niet laden welke andere kanalen worden gebruikt. Elk verzoek wacht nog steeds in FixControl.",
      add: "Goedkeuringskanaal toevoegen",
      used: "Gebruikt",
      notUsed: "Niet gebruikt",
      depends: "Hangt af van het soort goedkeuring",
      provider: { email: "E-mail", slack: "Slack", teams: "Microsoft Teams" },
      gateType: { plan: "AI-plan", risky_patch: "Codewijziging", deployment: "Uitrol" },
      reason: {
        route: "Gekozen door je goedkeuringsroute",
        originThread: "In de eigen Slack-thread van het verzoek",
        projectChannel: "Het kanaal van dit project",
        projectChannelNamed: "Het kanaal van dit project {target}",
        defaultChannel: "Het standaardkanaal van je werkruimte",
        emailOn: "Naar iedereen die kan beslissen",
        routeExcludes: "Je goedkeuringsroute gebruikt het niet",
        emailOff: "Goedkeurings-e-mail staat niet aan",
        mailNotConfigured: "Er is geen uitgaande mail ingesteld",
        notConnected: "Niet gekoppeld",
        noTarget: "Gekoppeld, maar er is geen kanaal om in te posten",
        noRecipients: "Niemand om het naar te sturen",
        always: "Altijd",
      },
      boundTitle: "Kanalen die voor dit project zijn ingesteld",
      bindingUnused: "Niet gebruikt voor goedkeuringsverzoeken: goedkeurings-e-mail gaat naar de mensen die kunnen beslissen, elk met eigen links. Je kunt dit verwijderen.",
      timeout: {
        none: "Geen tijdslimiet: een verzoek wacht tot iemand beslist.",
        rule: "Als niemand binnen {minutes} minuten beslist: {action}.",
        action: {
          escalate: "krijgen de managers een herinnering (de beheerders als er geen managers zijn)",
          notify_fallback: "krijgen de managers een herinnering (de beheerders als er geen managers zijn)",
          auto_reject: "wordt het verzoek automatisch afgewezen",
          pause: "blijft het wachten",
        },
        automatic: "Wordt elke vijf minuten automatisch gecontroleerd.",
        manual: "Wordt niet automatisch gecontroleerd: een beheerder kan dit aanzetten in de governance-instellingen van het bedrijf.",
      },
    },
    roleAssignments: {
      title: "Wie heeft welke rol",
      subtitle: "Met goedkeuring in fasen aan wordt een fase beslist door wie de rol van die fase heeft. Niemand → de terugvalrol van de fase → iedere beheerder of manager. Eén persoon keurt hooguit één fase van een verzoek goed, dus een route heeft evenveel verschillende beheerders en managers nodig als fasen. Alleen beheerders en managers kunnen een rol hebben.",
      unassigned: "Niemand — valt terug",
      noCandidates: "Niemand in je organisatie is een actieve beheerder of manager.",
      failed: "Kon dit niet wijzigen. Er is niets gewijzigd.",
    },
    automation: {
      title: "Hoe goedkeuringsregels worden toegepast",
      subtitle: "Vier schakelaars voor de hele organisatie. Elk ervan verandert wat werk moet doorlopen, dus elk vraagt eerst.",
      on: "Aan",
      off: "Uit",
      turnOn: "Aanzetten",
      turnOff: "Uitzetten",
      failed: "Kon dit niet wijzigen. Er is niets gewijzigd.",
      timeouts: {
        label: "Goedkeuringstime-outs en escalaties automatisch toepassen",
        hint: "Elke vijf minuten: goedkeuringen die langer wachtten dan je time-out krijgen de actie uit je beleid (een herinnering aan de managers, of de beheerders als die er niet zijn, of automatisch afwijzen), en je escalatieregels voor vastgelopen missies, inactieve issues, issues over hun tijdslimiet en opgeraakte retries treden in werking. Uit: ze doen alleen iets als iemand de governance-sweep draait.",
        confirmOn: "Goedkeuringstime-outs automatisch toepassen?",
        confirmOnBody: "Goedkeuringen die nu al te lang wachten worden bij de volgende controle afgehandeld, binnen vijf minuten.",
        confirmOff: "Goedkeuringstime-outs niet meer automatisch toepassen?",
        confirmOffBody: "Te late goedkeuringen blijven dan wachten tot iemand beslist of de governance-sweep draait.",
      },
      staged: {
        label: "Goedkeuring in fasen afdwingen",
        hint: "Een gate waarvan de goedkeuringsroute fasen heeft wordt fase voor fase beslist: door wie die rol heeft (zie Wie heeft welke rol), op volgorde, één fase per persoon, elk met een eigen deadline. Een nee wijst meteen af. Uit: één beslissing van een beheerder of manager rondt een gate af.",
        confirmOn: "Goedkeuring in fasen afdwingen?",
        confirmOnBody: "Nieuwe gates met fasen in hun route hebben elke fase nodig. Al geopende gates veranderen niet. Controleer eerst wie welke rol heeft; anders valt een fase terug op iedere beheerder of manager.",
        confirmOff: "Goedkeuring in fasen niet meer afdwingen?",
        confirmOffBody: "Nieuwe gates worden weer met één beslissing afgerond. Gates die openden terwijl dit aan stond blijven in fasen tot ze beslist zijn.",
      },
      engine: {
        label: "Risicoregels mogen een plangoedkeuring vereisen",
        hint: "Voordat een plan start worden je risicoregels, conflicten en veiligheidsinstellingen gecontroleerd op het project van de run; vereisen die goedkeuring, dan wacht het plan daarop, ook als het planbeleid alleen het zou laten starten. Dit kan alleen een goedkeuring toevoegen, nooit weghalen.",
        confirmOn: "Risicoregels plangoedkeuringen laten vereisen?",
        confirmOnBody: "Nieuwe plannen kunnen op goedkeuring wachten waar dat eerst niet zo was. Lopende plannen veranderen niet.",
        confirmOff: "Risicoregels geen plangoedkeuringen meer laten vereisen?",
        confirmOffBody: "Nieuwe plannen volgen weer alleen het planbeleid.",
      },
      relax: {
        label: "Projecten mogen minder streng zijn dan het bedrijfsbeleid",
        hint: "Uit: een eigen projectregel geldt alleen als die strenger is dan het bedrijfsbeleid. Aan: een geactiveerde projectregel geldt ook als die versoepelt.",
        confirmOn: "Projecten toestaan het bedrijfsbeleid te versoepelen?",
        confirmOnBody: "Geactiveerde projectregels die minder streng zijn dan het bedrijfsbeleid gaan meteen gelden voor nieuw werk.",
        confirmOff: "Alleen strengere projectregels toestaan?",
        confirmOffBody: "Projectregels die minder streng zijn dan het bedrijfsbeleid gelden meteen niet meer voor nieuw werk.",
      },
    },
    companyPage: {
      crumb: "Regels voor het hele bedrijf",
      bannerLead: "Je wijzigt de regels voor alle projecten. {project} kan ook eigen regels hebben op de",
      bannerLink: "pagina Goedkeuringen & regels",
      bannerTail: ".",
      back: "Terug naar Goedkeuringen & regels",
      forbidden: "Alleen een beheerder van je organisatie kan de regels voor alle projecten bekijken of wijzigen.",
    },
    companyNoAccess: "De regels voor alle projecten worden beheerd door een beheerder van je organisatie.",
    pointerFromUpdates: "Wie goedkeurt en waar dat gevraagd wordt: zie Goedkeuringen & regels",
    advanced: {
      summary: "Geavanceerd: technisch beleid",
      hint: "De beleidsvelden, waar ze vandaan komen en de keten van niveaus, zoals de beleidsmotor ze ziet.",
    },
    movedFromAdvanced: "Het goedkeuringsbeleid voor dit project staat nu onder Goedkeuringen & regels",
    loadError: "Kon de goedkeuringsregels voor dit project niet laden.",
    retry: "Opnieuw proberen",
  },
  projectAiTeam: {
    modeTitle: "Wat de AI op dit project met je code mag doen",
    modeChange: "Wijzigen onder Omgevingen",
    agentsTitle: "Agents die aan dit project werken",
    agentsHint: "Agents en hun vaardigheden worden gedeeld door alle projecten in je organisatie. Je beheert ze onder AI-team.",
    manage: "AI-team beheren",
    skillsLabel: "Vaardigheden",
    noSkills: "Geen extra vaardigheden",
    paused: "Gepauzeerd",
    loadError: "Kon het AI-team niet laden.",
    role: {
      intake: "Intake — maakt van binnenkomende e-mails, chats en tickets duidelijke issues met een prioriteit.",
      workflow: "Workflow — bepaalt voor elk issue de volgende stap en wie daarna aan zet is.",
      codebase: "Codebase — zoekt de code en geschiedenis op die voor een issue belangrijk zijn.",
      dev: "Ontwikkelaar — bereidt een codewijziging voor en probeert die uit in een veilige kopie.",
      cto: "Teamleider — plant het werk en beoordeelt risicovolle wijzigingen voordat ze verder gaan.",
      qa: "QA — controleert of een wijziging werkt en niets anders stukmaakt.",
      support: "Klantantwoord — schrijft conceptantwoorden aan klanten die een mens goedkeurt.",
      security: "Beveiliging — controleert wijzigingen op beveiligingsrisico's.",
      release: "Release — volgt een wijziging op weg naar productie.",
      ux: "Ontwerpcontrole — vergelijkt schermen voor en na een wijziging.",
      other: "Helpt mee met werk aan dit project.",
    },
  },
  projectMembers: {
    title: "Leden",
    subtitle: "Mensen die aan dit project werken, en wat ieder van hen hier mag doen.",
    orgWide: "Beheerders en managers zien alle projecten al, omdat zij het hele bedrijf beheren. Voeg hier andere collega's toe.",
    listTitle: "Mensen op dit project",
    empty: "Er is nog niemand toegevoegd.",
    emptyHint: "Collega's die geen beheerder of manager zijn, zien dit project pas als je ze toevoegt.",
    roleLabel: "Rol",
    noRole: "Geen projectrol",
    validUntilLabel: "Toegang tot (optioneel)",
    validUntilShort: "tot {date}",
    status: {
      active: "Actief",
      suspended: "Gepauzeerd",
      revoked: "Verwijderd",
    },
    roles: {
      admin: {
        name: "Projectbeheerder",
        explain: "Kan dit project koppelen aan mailboxen en tools die je organisatie al heeft, ze testen, leden toevoegen of verwijderen en werk goedkeuren. Het project hernoemen, nieuwe bedrijfsaccounts toevoegen en de goedkeuringsregels wijzigen doet een beheerder van je organisatie.",
      },
      approver: {
        name: "Goedkeurder",
        explain: "Kan wijzigingen en antwoorden op dit project goedkeuren, maar geen leden beheren.",
      },
      contributor: {
        name: "Medewerker",
        explain: "Kan aan de issues van dit project werken en conceptantwoorden schrijven, maar niets goedkeuren of instellingen wijzigen.",
      },
      viewer: {
        name: "Kijker",
        explain: "Kan dit project bekijken, maar niets wijzigen.",
      },
    },
    abilities: {
      changeSettings: "Kan ook de instellingen van dit project wijzigen.",
      manageConnections: "Kan ook de verbindingen van dit project beheren.",
      changeRules: "Kan ook de regels van dit project wijzigen.",
    },
    add: {
      title: "Iemand toevoegen",
      emailLabel: "E-mailadres",
      emailPlaceholder: "collega@bedrijf.nl",
      submit: "Aan project toevoegen",
      adding: "Bezig met toevoegen…",
      added: "{name} is aan dit project toegevoegd.",
    },
    changeRole: "Rol wijzigen",
    roleChanged: "Rol bijgewerkt.",
    remove: "Van project verwijderen",
    removeConfirmTitle: "{name} van dit project verwijderen?",
    removeConfirm: "Deze persoon ziet de issues van dit project dan niet meer. De verwijdering blijft in het auditlog staan, en je kunt de persoon later opnieuw toevoegen.",
    removed: "{name} is van dit project verwijderd.",
    invite: {
      button: "Iemand nieuw uitnodigen",
      title: "Iemand nieuw uitnodigen",
      intro: "Dit maakt een FixControl-account aan voor je hele organisatie en voegt de persoon daarna aan dit project toe.",
      nameLabel: "Volledige naam",
      emailLabel: "E-mailadres",
      passwordLabel: "Tijdelijk wachtwoord",
      passwordHint: "Minstens 8 tekens. Deel het zelf met de persoon; die kan het na het inloggen wijzigen.",
      weakPassword: "Het tijdelijke wachtwoord moet minstens 8 tekens hebben.",
      exists: "Er bestaat al een account met dit e-mailadres. Voeg het toe met het formulier hierboven.",
      submit: "Account maken en toevoegen",
      creating: "Account wordt gemaakt…",
      failed: "Kon het account niet maken. {reason}",
      cannot: "Alleen beheerders kunnen accounts maken voor nieuwe collega's. Vraag een beheerder om ze uit te nodigen en voeg ze daarna hier toe.",
      notFoundHint: "Er is nog geen account met dit e-mailadres.",
    },
    readOnly: "Je kunt zien wie aan dit project werkt. Alleen projectbeheerders, beheerders en managers kunnen dat wijzigen.",
    loadError: "Kon de leden van dit project niet laden.",
    retry: "Opnieuw proberen",
    rolesGap: "Je organisatie heeft nog geen rol {roles}.",
    errors: {
      lastAdmin: "Dit is de laatste projectbeheerder. Maak eerst iemand anders projectbeheerder.",
      selfEdit: "Je kunt je eigen toegang tot dit project niet wijzigen. Vraag een andere beheerder.",
      userNotFound: "Er is geen actief account met dit e-mailadres in je organisatie.",
      roleNotAssignable: "Deze rol kan niet op dit project worden gebruikt.",
      forbidden: "Je mag de leden van dit project niet wijzigen.",
      generic: "Er ging iets mis. Er is niets gewijzigd.",
    },
    wizard: {
      title: "Lid toevoegen",
      stepOf: "Stap {n} van {total}",
      stepPerson: "Wie",
      stepRole: "Rol",
      stepDone: "Klaar",
      next: "Volgende",
      back: "Terug",
      close: "Sluiten",
      done: "Klaar",
      finished: "{name} is nu lid van dit project als {role}.",
    },
  },
};
